/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useState } from 'react';

import { useQuery } from '@apollo/client';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Button, InlineNotification, Link, TextField, Typography2, breakpoints } from '@coursera/cds-core';

import Form from 'bundles/authentication-common/components/Form';
import LinkButton from 'bundles/authentication-common/components/LinkButton';
import Redirection from 'bundles/authentication-common/components/authenticationModal/OrganizationSSO/Redirection';
import OrganizationEmailDomainQuery from 'bundles/authentication-common/components/authenticationModal/OrganizationSSO/queries/GetThirdPartyOrganizationEmailDomainById.graphql';
import type {
  GetThirdPartyOrganizationEmailDomainByDomainIdQuery,
  GetThirdPartyOrganizationEmailDomainByDomainIdQueryVariables,
} from 'bundles/authentication-common/components/authenticationModal/OrganizationSSO/queries/__generated__/GetThirdPartyOrganizationEmailDomainById';
import { AUTHENTICATION_MODAL_MODES } from 'bundles/authentication-common/constants';
import type { AUTHENTICATION_MODAL_MODES_KEY } from 'bundles/authentication-common/constants';
import useTrackClickButton from 'bundles/authentication-common/hooks/useTrackClickButton';

import _t from 'i18n!nls/authentication';

type Props = {
  error?: string;
  ssoMode?: Extract<AUTHENTICATION_MODAL_MODES_KEY, 'organizationsignup' | 'organizationlogin'>;
  navigate: (path: AUTHENTICATION_MODAL_MODES_KEY, options?: $TSFixMe) => void;
  onSubmit: ({ url }: { url: string }) => void;
  hideBackToLink?: boolean;
  ssoRedirectTo?: string;
};

const styles = {
  container: css`
    display: grid;
    grid-gap: 0;
    grid-template-columns: 1fr;
    margin: 0 auto;
    max-width: 396px;
    padding: var(--cds-spacing-50);

    .cds-text-field-root {
      margin: var(--cds-spacing-150) 0 !important;
    }

    ${breakpoints.down('xs')} {
      padding: var(--cds-spacing-300) var(--cds-spacing-200);
    }
  `,
  form: css`
    display: grid;
    grid-gap: 0;
  `,
  navigation: css`
    margin-bottom: var(--cds-spacing-150);
  `,
  submit: css`
    margin: var(--cds-spacing-300) 0 var(--cds-spacing-200) 0;

    ${breakpoints.down('xs')} {
      margin-top: var(--cds-spacing-400);
    }
  `,
  inputSection: css`
    display: flex;
    flex-direction: column;
    gap: 0;
  `,
  header: css`
    margin-right: var(--cds-spacing-300);
  `,
  title: css`
    margin-bottom: var(--cds-spacing-300);

    ${breakpoints.down('xs')} {
      margin-bottom: var(--cds-spacing-150);
    }
  `,
};

export const OrganizationSSO = (props: Props) => {
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [url, setUrl] = useState<string | null>(null);
  const [hasError, setHasError] = useState<boolean>(false);
  const { trackButton } = useTrackClickButton();

  const isSignup = props.ssoMode === AUTHENTICATION_MODAL_MODES.ORGANIZATIONSIGNUP;

  const navigateBack = () => {
    const { navigate } = props;

    const navigateTarget = isSignup ? AUTHENTICATION_MODAL_MODES.SIGNUP : AUTHENTICATION_MODAL_MODES.LOGIN;

    trackButton({
      name: isSignup ? 'back_to_signup_form' : 'back_to_login_form',
    });
    return navigate(navigateTarget);
  };

  const handleOnInvalid = () => {
    trackButton({ name: isSignup ? 'signup_with_organization' : 'login_with_organization', clickStatus: 'invalid' });
  };

  const { hideBackToLink = false } = props;

  const { data, error, loading } = useQuery<
    GetThirdPartyOrganizationEmailDomainByDomainIdQuery,
    GetThirdPartyOrganizationEmailDomainByDomainIdQueryVariables
  >(OrganizationEmailDomainQuery, {
    skip: !url,
    variables: { id: url || '' },
    context: { clientName: 'gatewayGql' },
  });

  const title = isSignup ? _t('Sign up with your organization') : _t('Log in with your organization');
  const navigateTo = isSignup ? _t('Sign Up') : _t('Log in');

  const getByIdResponse = data?.ThirdPartyOrganizationEmailDomain?.getById;
  const id =
    getByIdResponse?.__typename === 'ThirdPartyOrganizationEmailDomain_EmailDomain'
      ? getByIdResponse.thirdPartyOrganizationId
      : null;

  if (!loading && id) {
    trackButton({ name: isSignup ? 'signup_with_organization' : 'login_with_organization', clickStatus: 'valid' });
    return <Redirection {...props} id={id} redirect={props.ssoRedirectTo} />;
  }

  if (!loading && error) {
    trackButton({ name: isSignup ? 'signup_with_organization' : 'login_with_organization', clickStatus: 'invalid' });
    setHasError(true);
    setUrl(null);
  }

  // If the query completed but there is no id, it means the organization was not found
  if (!loading && data && !id) {
    trackButton({ name: isSignup ? 'signup_with_organization' : 'login_with_organization', clickStatus: 'invalid' });
    setHasError(true);
    setUrl(null);
  }

  return (
    <section className="rc-OrganizationSSO" css={styles.container}>
      <header css={styles.header}>
        <Typography2 variant="titleXSmall" component="h1" align="left" css={styles.title}>
          {title}
        </Typography2>
      </header>
      <Form
        css={styles.form}
        onSubmit={({ url: urlInput }) => {
          // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          setUrl(urlInput);
        }}
        onInput={(e) => {
          const formRef = e.currentTarget;
          setIsButtonDisabled(!formRef.checkValidity());
        }}
        onInvalid={handleOnInvalid}
      >
        {hasError && (
          <InlineNotification severity="error" title={_t('Organization not found')} role="alert">
            {_t('Please enter a valid organization and ensure it is formatted correctly')}
          </InlineNotification>
        )}
        <div css={styles.inputSection}>
          <TextField
            autoComplete="organization"
            label={_t('Enter Your Organization URL')}
            name="url"
            placeholder={_t('(ex: company.com or company.org)')}
          />
          <Link
            typographyVariant="bodySecondary"
            href="https://learner.coursera.help/hc/articles/115004807263"
            rel="noopener noreferrer"
            target="_blank"
          >
            {_t('Help')}
          </Link>
        </div>

        <Button fullWidth={true} disabled={isButtonDisabled} type="submit" css={styles.submit}>
          <Typography2 component="span" variant="actionPrimary" color="inherit">
            {_t('Continue')}
          </Typography2>
        </Button>
      </Form>

      {!hideBackToLink && (
        <Typography2 component="p" variant="bodySecondary" css={styles.navigation} align="left">
          <FormattedMessage
            message={_t('{backTo} {backToLink}.')}
            backTo={<span aria-hidden="true">{_t('Back to')}</span>}
            backToLink={
              <LinkButton onClick={navigateBack} type="button">
                {navigateTo}
              </LinkButton>
            }
          />
        </Typography2>
      )}
    </section>
  );
};

export default OrganizationSSO;
